/**
 * <s-delivery-estimate> — fills in an estimated delivery date range.
 *
 * Counts business days in New York time, skipping weekends and US federal
 * holidays (with Saturday holidays observed Friday, Sunday holidays Monday).
 * Orders placed on a business day before the cutoff hour count today as the
 * first processing day. Shipping starts the business day after processing ends.
 *
 * Data attributes: processing-days, transit-min, transit-max, cutoff-hour, locale.
 * Writes the range into the descendant marked data-delivery-dates.
 */
const TIME_ZONE = 'America/New_York';
const DAY_MS = 86400000;

function utcDate(year, month, day) {
  return new Date(Date.UTC(year, month, day));
}

function nthWeekday(year, month, weekday, n) {
  const first = utcDate(year, month, 1);
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  return utcDate(year, month, 1 + offset + (n - 1) * 7);
}

function lastWeekday(year, month, weekday) {
  const last = utcDate(year, month + 1, 0);
  const offset = (last.getUTCDay() - weekday + 7) % 7;
  return utcDate(year, month, last.getUTCDate() - offset);
}

function observed(date) {
  const day = date.getUTCDay();
  if (day === 6) return new Date(date.getTime() - DAY_MS);
  if (day === 0) return new Date(date.getTime() + DAY_MS);
  return date;
}

function key(date) {
  return date.toISOString().slice(0, 10);
}

const holidayCache = new Map();

function holidaysFor(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const dates = [
    observed(utcDate(year, 0, 1)), // New Year's Day
    nthWeekday(year, 0, 1, 3), // Martin Luther King Jr. Day
    nthWeekday(year, 1, 1, 3), // Presidents' Day
    lastWeekday(year, 4, 1), // Memorial Day
    observed(utcDate(year, 5, 19)), // Juneteenth
    observed(utcDate(year, 6, 4)), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor Day
    nthWeekday(year, 9, 1, 2), // Columbus Day
    observed(utcDate(year, 10, 11)), // Veterans Day
    nthWeekday(year, 10, 4, 4), // Thanksgiving
    observed(utcDate(year, 11, 25)), // Christmas Day
  ];
  // New Year's Day of next year can be observed on Dec 31 of this year.
  dates.push(observed(utcDate(year + 1, 0, 1)));
  const set = new Set(dates.map(key));
  holidayCache.set(year, set);
  return set;
}

function isBusinessDay(date) {
  const day = date.getUTCDay();
  if (day === 0 || day === 6) return false;
  return !holidaysFor(date.getUTCFullYear()).has(key(date));
}

function nextBusinessDay(date) {
  let next = new Date(date.getTime() + DAY_MS);
  while (!isBusinessDay(next)) next = new Date(next.getTime() + DAY_MS);
  return next;
}

function addBusinessDays(date, count) {
  let result = date;
  for (let i = 0; i < count; i += 1) result = nextBusinessDay(result);
  return result;
}

function nowInNewYork() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const value = (type) => Number(parts.find((part) => part.type === type)?.value);
  return { date: utcDate(value('year'), value('month') - 1, value('day')), hour: value('hour') };
}

class SDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-delivery-dates]');
    if (!output) return;

    const processing = Math.max(0, Number(this.dataset.processingDays) || 0);
    const transitMin = Math.max(1, Number(this.dataset.transitMin) || 1);
    const transitMax = Math.max(transitMin, Number(this.dataset.transitMax) || transitMin);
    const cutoff = Number(this.dataset.cutoffHour);

    const { date: today, hour } = nowInNewYork();
    const startsToday = isBusinessDay(today) && hour < (Number.isFinite(cutoff) ? cutoff : 12);

    // Last processing day: day the order ships.
    let shipDay;
    if (processing === 0) {
      shipDay = startsToday ? today : nextBusinessDay(today);
    } else {
      const firstDay = startsToday ? today : nextBusinessDay(today);
      shipDay = addBusinessDays(firstDay, processing - 1);
    }

    const earliest = addBusinessDays(shipDay, transitMin);
    const latest = addBusinessDays(shipDay, transitMax);

    let locale = this.dataset.locale || document.documentElement.lang || 'en-US';
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    } catch {
      formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    }

    output.textContent =
      key(earliest) === key(latest) || typeof formatter.formatRange !== 'function'
        ? key(earliest) === key(latest)
          ? formatter.format(earliest)
          : `${formatter.format(earliest)} – ${formatter.format(latest)}`
        : formatter.formatRange(earliest, latest);

    this.hidden = false;
  }
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', SDeliveryEstimate);
}
